<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
        <div>
          <h2 class="text-xl font-bold text-secondary-900 dark:text-white">
            {{ isEditing ? 'Editar plantilla' : 'Nueva plantilla' }}
          </h2>
          <p class="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
            Crea plantillas con campos dinámicos que se reemplazan automáticamente
          </p>
        </div>
        <button @click="handleClose" class="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors">
          <X class="w-5 h-5 text-secondary-500" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-hidden flex">
        <!-- Left: Editor -->
        <div class="flex-1 flex flex-col overflow-hidden border-r border-secondary-200 dark:border-secondary-700">
          <div class="px-6 py-4 space-y-4 overflow-y-auto flex-1">
            <!-- Nombre -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Nombre de la plantilla
              </label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Ej: Notificación de embarque, Seguimiento OC..."
                class="w-full px-3 py-2 border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            <!-- Descripción -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Descripción <span class="text-secondary-400 font-normal">(opcional)</span>
              </label>
              <input
                v-model="form.description"
                type="text"
                placeholder="Breve descripción del uso de esta plantilla"
                class="w-full px-3 py-2 border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            <!-- Destinatarios por defecto -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                {{ t('templateEditor.defaultRecipients.label') }}
              </label>
              <p class="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                {{ t('templateEditor.defaultRecipients.help') }}
              </p>
              <div class="space-y-2">
                <div v-for="field in RECIPIENT_FIELDS" :key="field" class="flex items-start gap-2">
                  <span class="w-10 pt-2 text-xs font-medium text-secondary-500 dark:text-secondary-400">
                    {{ t(`templateEditor.defaultRecipients.${field}`) }}
                  </span>
                  <RecipientInput
                    v-model="form.defaultRecipients[field]"
                    v-model:input-value="recipientInputs[field]"
                    :placeholder="t('emailComposer.recipientPlaceholder')"
                    group="template-recipients"
                    :dragging="recipientDragging"
                    @parse="parseDefaultRecipients(field)"
                    @drag-start="recipientDragging = true"
                    @drag-end="recipientDragging = false"
                  />
                </div>
              </div>
            </div>

            <!-- Auto-llenar campo de fecha -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Auto-llenar campo de fecha <span class="text-secondary-400 font-normal">(opcional)</span>
              </label>
              <p class="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                Selecciona un campo de fecha que se llenará automáticamente la primera vez que envíes esta plantilla
              </p>
              <div class="flex gap-2">
                <select
                  v-model="autoFillDateModule"
                  class="px-3 py-2 text-sm border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  @change="autoFillDateFieldId = ''"
                >
                  <option value="">Sin auto-llenado</option>
                  <option value="shipments">Embarques</option>
                  <option value="purchaseOrders">Órdenes de Compra</option>
                </select>
                <select
                  v-if="autoFillDateModule"
                  v-model="autoFillDateFieldId"
                  class="flex-1 px-3 py-2 text-sm border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Seleccionar campo...</option>
                  <optgroup
                    v-for="section in dateFieldSections"
                    :key="section"
                    :label="section"
                  >
                    <option
                      v-for="field in dateFieldsBySection(section)"
                      :key="field.id"
                      :value="field.name || field.id"
                    >
                      {{ field.label }}
                    </option>
                  </optgroup>
                </select>
              </div>
            </div>

            <!-- Auto-adjuntar documentos por tipo -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                {{ t('templateEditor.autoAttach.label') }}
              </label>
              <p class="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                {{ t('templateEditor.autoAttach.help') }}
              </p>
              <div class="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-secondary-200 dark:border-secondary-700 rounded">
                <button
                  v-for="dt in autoAttachTypeOptions"
                  :key="dt.id"
                  type="button"
                  @click="toggleAutoAttachType(dt.id)"
                  :class="[
                    'px-2 py-1 text-xs rounded border transition-colors',
                    form.autoAttachDocumentTypes.includes(dt.id)
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border-secondary-300 dark:border-secondary-600'
                  ]"
                >
                  {{ getDocumentTypeName(dt.id, locale) }}
                </button>
              </div>
            </div>

            <!-- Visibilidad -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Visibilidad
              </label>
              <div class="flex gap-3">
                <button
                  type="button"
                  @click="form.visibility = 'public'"
                  class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors"
                  :class="form.visibility === 'public'
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                    : 'bg-white dark:bg-secondary-900 border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-800'"
                >
                  <Globe class="w-4 h-4" />
                  <span>Pública</span>
                </button>
                <button
                  type="button"
                  @click="form.visibility = 'private'"
                  class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors"
                  :class="form.visibility === 'private'
                    ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300'
                    : 'bg-white dark:bg-secondary-900 border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-800'"
                >
                  <Lock class="w-4 h-4" />
                  <span>Solo yo</span>
                </button>
              </div>
              <p class="text-xs text-secondary-400 dark:text-secondary-500 mt-1">
                {{ form.visibility === 'public' ? 'Visible para todos los usuarios de la compañía' : 'Solo visible para ti' }}
              </p>
            </div>

            <!-- Asunto -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Asunto del correo
              </label>
              <div class="flex gap-2">
                <input
                  v-model="form.subject"
                  type="text"
                  placeholder="Ej: Actualización de embarque {{shipments.idNumber}}"
                  class="flex-1 px-3 py-2 border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
                <div class="relative" ref="subjectFieldDropdownRef">
                  <button
                    @click="showSubjectFieldDropdown = !showSubjectFieldDropdown"
                    class="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium whitespace-nowrap"
                    title="Insertar campo dinámico en el asunto"
                  >
                    + Campo
                  </button>
                  <div
                    v-if="showSubjectFieldDropdown"
                    class="absolute right-0 top-full mt-1 w-80 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl z-50 p-3"
                  >
                    <DynamicFieldPicker @select="insertSubjectField" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Toolbar del editor -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Cuerpo del correo
              </label>
              <div class="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
                <RichTextToolbar
                  class="px-3 bg-secondary-50 dark:bg-secondary-900"
                  :active-formats="activeFormats"
                  @toggle="toggle"
                  @font-family="setFontFamily"
                  @font-size="setFontSize"
                  @text-color="setTextColor"
                  @highlight="setHighlight"
                  @clear-format="clearFormat"
                  :read-link-context="readLinkContext"
                  @link="insertLink($event.url, $event.text)"
                  @unlink="removeLink"
                >
                  <template #extra>
                    <div class="w-px h-[18px] bg-secondary-200 dark:bg-secondary-700 mx-1"></div>

                    <!-- Estilo de párrafo -->
                    <select
                      @change="applyHeading($event)"
                      class="px-2 py-1 text-xs border border-secondary-200 dark:border-secondary-700 rounded bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300"
                    >
                      <option value="">Párrafo</option>
                      <option value="h1">Título 1</option>
                      <option value="h2">Título 2</option>
                      <option value="h3">Título 3</option>
                    </select>

                    <!-- Botón insertar campo dinámico -->
                    <button
                      @mousedown.prevent
                      @click="showBodyFieldPanel = !showBodyFieldPanel"
                      class="px-2.5 py-1 text-xs font-semibold rounded transition-colors flex items-center gap-1"
                      :class="showBodyFieldPanel
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100'"
                      title="Insertar campo dinámico"
                    >
                      <Database class="w-3.5 h-3.5" />
                      <span>Campo dinámico</span>
                    </button>
                  </template>
                </RichTextToolbar>

                <!-- Editor contenteditable -->
                <div
                  ref="editorRef"
                  contenteditable="true"
                  @input="onEditorInput"
                  @paste="onEditorPaste"
                  @keydown="handleEditorKeydown"
                  class="rich-text-content min-h-[280px] max-h-[400px] overflow-y-auto px-4 py-3 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:outline-none text-sm leading-relaxed"
                  :class="{ 'text-secondary-400': !form.bodyHtml }"
                  data-placeholder="Escribe el contenido del correo aquí. Usa el botón 'Campo dinámico' para insertar campos que se reemplazan automáticamente..."
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Field picker panel (visible al hacer click en "Campo dinámico") -->
        <div
          v-if="showBodyFieldPanel"
          class="w-80 flex-shrink-0 bg-secondary-50 dark:bg-secondary-900 overflow-y-auto p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-bold text-secondary-900 dark:text-white">Insertar campo</h3>
            <button @click="showBodyFieldPanel = false" class="p-1 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded">
              <X class="w-4 h-4 text-secondary-500" />
            </button>
          </div>
          <DynamicFieldPicker @select="insertBodyField" @insert-table="insertBodyTable" />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
        <div class="flex items-center gap-3 text-sm text-secondary-500">
          <span v-if="usedFieldsCount > 0" class="flex items-center gap-1">
            <Database class="w-4 h-4 text-blue-500" />
            {{ usedFieldsCount }} campo(s) dinámico(s)
          </span>
          <span v-if="usedModules.length > 0">
            Módulos: {{ usedModules.join(', ') }}
          </span>
        </div>
        <div class="flex items-center gap-3">
          <button
            @click="handleClose"
            class="px-4 py-2 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors text-sm font-medium"
          >
            Cancelar
          </button>
          <button
            @click="handleSave"
            :disabled="!canSave || isSaving"
            class="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
          >
            <Save class="w-4 h-4" />
            {{ isSaving ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Crear plantilla') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  X, Database, Save, Globe, Lock,
} from 'lucide-vue-next'
import { useRichTextFormat } from '@/composables/useRichTextFormat'
import RichTextToolbar from '../editor/RichTextToolbar.vue'
import DynamicFieldPicker from './DynamicFieldPicker.vue'
import RecipientInput from '@/components/email/RecipientInput.vue'
import { useTemplateResolver } from '@/composables/useTemplateResolver'
import { useEmailTemplateStore } from '@/stores/emailTemplate'
import { useAuthStore } from '@/stores/auth'
import { useSystemFieldsConfig } from '@/composables/useSystemFieldsConfig'
import { useCompanyStore } from '@/stores/company'
import { useDocumentTypes } from '@/composables/useDocumentTypes'
import { parseRecipientInput, pasteSanitizedHtml } from '@/utils/email'
import type { EmailAddress } from '@/types'
import type { EmailTemplate, TemplateDynamicField, TemplateModuleSource, TemplateVisibility, TemplateDynamicTableColumn, TemplateDefaultRecipients } from '@/types/emailTemplate'

const props = defineProps<{
  template?: EmailTemplate | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved', template: EmailTemplate): void
}>()

const templateStore = useEmailTemplateStore()
const authStore = useAuthStore()
const companyStore = useCompanyStore()
const { createFieldChipHtml, createTableChipHtml, extractDynamicFields } = useTemplateResolver()
const { getModuleFields, loadSystemFieldsConfig } = useSystemFieldsConfig()
const { documentTypes, isTypeVisible, ensureLoaded: ensureDocumentTypesLoaded, getDocumentTypeName } = useDocumentTypes()
const { t, locale } = useI18n()

const isEditing = computed(() => !!props.template?.id)
const isSaving = ref(false)
const showSubjectFieldDropdown = ref(false)
const showBodyFieldPanel = ref(false)
const subjectFieldDropdownRef = ref<HTMLElement | null>(null)
const editorRef = ref<HTMLElement | null>(null)
const savedRange = ref<Range | null>(null)

const RECIPIENT_FIELDS = ['to', 'cc', 'bcc'] as const
type RecipientField = (typeof RECIPIENT_FIELDS)[number]

const form = ref({
  name: '',
  description: '',
  subject: '',
  bodyHtml: '',
  visibility: 'public' as TemplateVisibility,
  autoAttachDocumentTypes: [] as string[],
  defaultRecipients: { to: [], cc: [], bcc: [] } as TemplateDefaultRecipients,
})

// Texto que el usuario está escribiendo en cada campo, antes de volverse ficha
const recipientInputs = ref<Record<RecipientField, string>>({ to: '', cc: '', bcc: '' })
// Hay una ficha en el aire: los tres campos muestran su zona de soltado
const recipientDragging = ref(false)

const autoFillDateModule = ref<TemplateModuleSource | ''>('')
const autoFillDateFieldId = ref('')

// Listener global para rastrear la posición del cursor dentro del editor
function onDocumentSelectionChange() {
  if (isInsertingField) return
  const selection = window.getSelection()
  if (selection && selection.rangeCount > 0 && editorRef.value) {
    const range = selection.getRangeAt(0)
    if (editorRef.value.contains(range.commonAncestorContainer)) {
      savedRange.value = range.cloneRange()
    }
  }
}

onMounted(async () => {
  document.addEventListener('selectionchange', onDocumentSelectionChange)

  // Cargar campos del sistema
  const companyId = companyStore.selectedCompanyId
  if (companyId) {
    await loadSystemFieldsConfig(companyId, 'shipments')
    await loadSystemFieldsConfig(companyId, 'purchaseOrders')
  }

  await ensureDocumentTypesLoaded()

  if (props.template) {
    form.value.name = props.template.name
    form.value.description = props.template.description || ''
    form.value.subject = props.template.subject
    form.value.bodyHtml = props.template.bodyHtml
    form.value.visibility = props.template.visibility || 'public'
    form.value.autoAttachDocumentTypes = props.template.autoAttachDocumentTypes
      ? [...props.template.autoAttachDocumentTypes]
      : []
    RECIPIENT_FIELDS.forEach((field) => {
      form.value.defaultRecipients[field] = [...(props.template?.defaultRecipients?.[field] || [])]
    })

    // Cargar autoFillDateField si existe
    if (props.template.autoFillDateField) {
      autoFillDateModule.value = props.template.autoFillDateField.module
      autoFillDateFieldId.value = props.template.autoFillDateField.fieldId
    }

    nextTick(() => {
      if (editorRef.value) {
        editorRef.value.innerHTML = props.template!.bodyHtml
      }
    })
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('selectionchange', onDocumentSelectionChange)
})

const dynamicFields = computed(() => {
  return extractDynamicFields(form.value.bodyHtml)
})

const usedFieldsCount = computed(() => dynamicFields.value.length)

const usedModules = computed(() => {
  const modules = new Set<string>()
  dynamicFields.value.forEach((f) => {
    modules.add(f.module === 'shipments' ? 'Embarques' : 'Órdenes de Compra')
  })
  // También buscar en el subject
  const subjectMatches = form.value.subject.match(/\{\{(\w+)\./g) || []
  subjectMatches.forEach((m) => {
    const mod = m.replace('{{', '').replace('.', '')
    if (mod === 'shipments') modules.add('Embarques')
    if (mod === 'purchaseOrders') modules.add('Órdenes de Compra')
  })
  return Array.from(modules)
})

// Computed para obtener campos de fecha disponibles
const dateFields = computed(() => {
  if (!autoFillDateModule.value) return []
  const fields = getModuleFields.value(autoFillDateModule.value)
  return fields.filter(f => f.type === 'date' || f.type === 'datetime')
})

const dateFieldSections = computed(() => {
  const sections = new Set<string>()
  dateFields.value.forEach(f => {
    sections.add(f.section || 'otros')
  })
  return Array.from(sections)
})

const dateFieldsBySection = (section: string) => {
  return dateFields.value.filter(f => (f.section || 'otros') === section)
}

const canSave = computed(() => {
  return form.value.name.trim() !== '' && form.value.subject.trim() !== ''
})

function onEditorInput() {
  if (editorRef.value) {
    normalizeFontSize()
    form.value.bodyHtml = editorRef.value.innerHTML
  }
}

function onEditorPaste(event: ClipboardEvent) {
  if (pasteSanitizedHtml(event)) onEditorInput()
}

// Tipos ocultos por la compañía no se ofrecen, salvo que la plantilla ya los use.
const autoAttachTypeOptions = computed(() =>
  documentTypes.value.filter(dt => isTypeVisible(dt.id) || form.value.autoAttachDocumentTypes.includes(dt.id))
)

/** Convierte en fichas lo escrito en un campo de destinatarios; lo inválido se queda visible */
function parseDefaultRecipients(field: RecipientField) {
  const input = recipientInputs.value[field]
  if (!input.trim()) return

  const { valid, invalid } = parseRecipientInput(input)
  const existing = new Set(form.value.defaultRecipients[field].map((r) => r.email.toLowerCase()))
  // Arreglo nuevo, no push: la lista de arrastre solo recalcula sus índices cuando cambia la referencia
  form.value.defaultRecipients[field] = [...form.value.defaultRecipients[field], ...valid.filter((r) => !existing.has(r.email.toLowerCase()))]
  recipientInputs.value[field] = invalid.join('; ')
}

/** Firestore rechaza `undefined`: el nombre solo viaja si tiene valor */
function buildDefaultRecipients(): TemplateDefaultRecipients {
  // Guardar sin salir del campo no debe perder lo que se acaba de escribir
  RECIPIENT_FIELDS.forEach(parseDefaultRecipients)

  const clean = (list: EmailAddress[]): EmailAddress[] =>
    list.map((r) => (r.name ? { email: r.email, name: r.name } : { email: r.email }))
  return {
    to: clean(form.value.defaultRecipients.to),
    cc: clean(form.value.defaultRecipients.cc),
    bcc: clean(form.value.defaultRecipients.bcc),
  }
}

function toggleAutoAttachType(id: string) {
  const list = form.value.autoAttachDocumentTypes
  const idx = list.indexOf(id)
  if (idx === -1) list.push(id)
  else list.splice(idx, 1)
}

function applyHeading(event: Event) {
  const select = event.target as HTMLSelectElement
  const value = select.value
  if (value) {
    document.execCommand('formatBlock', false, value)
  } else {
    document.execCommand('formatBlock', false, 'p')
  }
  select.value = ''
  editorRef.value?.focus()
}

const {
  activeFormats,
  toggle,
  setFontFamily,
  setFontSize,
  normalizeFontSize,
  setTextColor,
  setHighlight,
  clearFormat,
  insertLink,
  removeLink,
  linkAtSelection,
} = useRichTextFormat(editorRef, onEditorInput)

/** Lo que el diálogo de enlace muestra al abrirse. */
function readLinkContext() {
  const link = linkAtSelection()
  return {
    url: link?.getAttribute('href') || '',
    text: link?.textContent || document.getSelection()?.toString() || '',
  }
}

function handleEditorKeydown(e: KeyboardEvent) {
  // Evitar que se pueda editar dentro de un chip de campo dinámico
  const selection = window.getSelection()
  if (selection && selection.anchorNode) {
    const parent = selection.anchorNode.parentElement
    if (parent?.classList.contains('dynamic-field')) {
      if (e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        e.preventDefault()
      }
    }
  }
}

function insertSubjectField(field: TemplateDynamicField) {
  const placeholder = `{{${field.module}.${field.fieldId}}}`
  form.value.subject += placeholder
  showSubjectFieldDropdown.value = false
}

let isInsertingField = false

function insertBodyField(field: TemplateDynamicField) {
  const chipHtml = createFieldChipHtml(field)

  if (!editorRef.value) return

  isInsertingField = true

  const selection = window.getSelection()

  // Gracias a @mousedown.prevent, el editor mantiene foco y selección
  if (selection && selection.rangeCount > 0 && editorRef.value.contains(selection.getRangeAt(0).startContainer)) {
    const range = selection.getRangeAt(0)
    range.deleteContents()
    const temp = document.createElement('div')
    temp.innerHTML = chipHtml + '&nbsp;'
    const frag = document.createDocumentFragment()
    let node: ChildNode | null
    let lastNode: Node | null = null
    while ((node = temp.firstChild)) {
      lastNode = frag.appendChild(node)
    }
    range.insertNode(frag)
    if (lastNode) {
      range.setStartAfter(lastNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }
  } else if (savedRange.value && selection) {
    // Fallback: restaurar rango guardado (si el editor perdió foco por otra razón)
    editorRef.value.focus()
    selection.removeAllRanges()
    selection.addRange(savedRange.value)
    const range = selection.getRangeAt(0)
    range.deleteContents()
    const temp = document.createElement('div')
    temp.innerHTML = chipHtml + '&nbsp;'
    const frag = document.createDocumentFragment()
    let node: ChildNode | null
    let lastNode: Node | null = null
    while ((node = temp.firstChild)) {
      lastNode = frag.appendChild(node)
    }
    range.insertNode(frag)
    if (lastNode) {
      range.setStartAfter(lastNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }
  } else {
    // Último fallback: agregar al final
    editorRef.value.focus()
    editorRef.value.innerHTML += chipHtml + '&nbsp;'
  }

  form.value.bodyHtml = editorRef.value.innerHTML
  isInsertingField = false
}

function insertBodyTable(payload: { module: TemplateModuleSource; columns: TemplateDynamicTableColumn[]; source?: string }) {
  const tableHtml = createTableChipHtml(payload.module, payload.columns, payload.source)
  if (!editorRef.value) return

  const selection = window.getSelection()
  const html = '<br>' + tableHtml + '<br>'

  function insertHtmlAtRange(range: Range) {
    range.deleteContents()
    const temp = document.createElement('div')
    temp.innerHTML = html
    const frag = document.createDocumentFragment()
    let node: ChildNode | null
    let lastNode: Node | null = null
    while ((node = temp.firstChild)) {
      lastNode = frag.appendChild(node)
    }
    range.insertNode(frag)
    if (lastNode) {
      range.setStartAfter(lastNode)
      range.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
  }

  if (selection && selection.rangeCount > 0 && editorRef.value.contains(selection.getRangeAt(0).startContainer)) {
    insertHtmlAtRange(selection.getRangeAt(0))
  } else if (savedRange.value && selection) {
    editorRef.value.focus()
    selection.removeAllRanges()
    selection.addRange(savedRange.value)
    insertHtmlAtRange(selection.getRangeAt(0))
  } else {
    editorRef.value.focus()
    const wrapper = document.createElement('div')
    wrapper.innerHTML = html
    editorRef.value.appendChild(wrapper)
  }

  form.value.bodyHtml = editorRef.value.innerHTML
}

async function handleSave() {
  if (!canSave.value) return
  isSaving.value = true

  try {
    const allDynamicFields = extractDynamicFields(form.value.bodyHtml)

    // Extraer módulos del subject también
    const subjectModules: TemplateModuleSource[] = []
    const subjectMatches = form.value.subject.match(/\{\{(\w+)\./g) || []
    subjectMatches.forEach((m) => {
      const mod = m.replace('{{', '').replace('.', '') as TemplateModuleSource
      if (!subjectModules.includes(mod)) subjectModules.push(mod)
    })

    // Unificar módulos
    const allModules = new Set<TemplateModuleSource>([
      ...allDynamicFields.map((f) => f.module),
      ...subjectModules,
    ])

    const userId = authStore.user?.uid || 'unknown'

    // Construir autoFillDateField
    let autoFillDateField: { module: TemplateModuleSource; fieldId: string; fieldLabel: string } | null = null
    if (autoFillDateModule.value && autoFillDateFieldId.value) {
      const field = dateFields.value.find(f => f.id === autoFillDateFieldId.value || f.name === autoFillDateFieldId.value)
      if (field) {
        autoFillDateField = {
          module: autoFillDateModule.value,
          fieldId: field.name || field.id, // Usar field.name (path en Firestore) en lugar de autoFillDateFieldId
          fieldLabel: field.label || field.name || autoFillDateFieldId.value
        }
      } else {
        console.warn('⚠️ Campo de fecha no encontrado:', autoFillDateFieldId.value)
      }
    }

    if (isEditing.value && props.template?.id) {
      const success = await templateStore.updateTemplate(
        props.template.id,
        {
          name: form.value.name,
          description: form.value.description,
          subject: form.value.subject,
          bodyHtml: form.value.bodyHtml,
          modules: Array.from(allModules),
          dynamicFields: allDynamicFields,
          visibility: form.value.visibility,
          autoFillDateField,
          autoAttachDocumentTypes: form.value.autoAttachDocumentTypes,
          defaultRecipients: buildDefaultRecipients(),
        },
        userId
      )

      if (success) {
        emit('saved', {
          ...props.template,
          ...form.value,
          modules: Array.from(allModules),
          dynamicFields: allDynamicFields,
        })
      }
    } else {
      const newId = await templateStore.createTemplate(
        {
          name: form.value.name,
          description: form.value.description,
          subject: form.value.subject,
          bodyHtml: form.value.bodyHtml,
          modules: Array.from(allModules),
          dynamicFields: allDynamicFields,
          visibility: form.value.visibility,
          autoFillDateField: autoFillDateField || undefined,
          autoAttachDocumentTypes: form.value.autoAttachDocumentTypes,
          defaultRecipients: buildDefaultRecipients(),
        },
        userId
      )

      if (newId) {
        const created = templateStore.templates.find((t) => t.id === newId)
        if (created) emit('saved', created)
      }
    }
  } catch (err) {
    console.error('Error saving template:', err)
  } finally {
    isSaving.value = false
  }
}

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.toolbar-btn {
  @apply p-1.5 rounded hover:bg-secondary-200 dark:hover:bg-secondary-700 text-secondary-600 dark:text-secondary-400 transition-colors;
}

/* Placeholder para el editor */
[contenteditable="true"]:empty::before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}
</style>
